'use client';

import { useEffect } from 'react';
import { Study } from '@/lib/study';
import { T } from '@/lib/tokens';
import { Nav, StepHead, StepWrap } from '../StepShell';
import { Textarea } from '../Textarea';

const DEFAULT_OPENING = `Hello, and thank you for taking the time to speak with me today. I'm an AI interviewer, and I'm part of a research study.

In this interview, I'll ask you a few questions about your experiences. There are no right or wrong answers — I'm simply here to listen and learn from your perspective.

Some questions may feel personal, but please feel free to share only what you are comfortable with. This conversation should take about 20 minutes.

Before we begin, do you have any questions?`;

type Props = {
  data: Study;
  setData: (d: Study) => void;
  onBack: () => void;
  onNext: () => void;
};

export function Step8Opening({ data, setData, onBack, onNext }: Props) {
  useEffect(() => {
    if (!data.opening) setData({ ...data, opening: DEFAULT_OPENING });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StepWrap>
      <StepHead
        label="Step 9 — Opening"
        title="How should the interview begin?"
        subtitle="Write the opening message that participants hear first. Set the tone, explain the purpose, and invite questions before you start."
      />
      <Textarea
        value={data.opening || DEFAULT_OPENING}
        onChange={(v) => setData({ ...data, opening: v })}
        rows={10}
        label="Opening message"
        helper="This will be read aloud by the AI interviewer at the start of each session."
      />
      <div
        style={{
          background: T.terraLight,
          border: `1px solid ${T.terraMid}`,
          borderRadius: 10,
          padding: '12px 16px',
          fontSize: 13,
          color: T.espressoMid,
        }}
      >
        <strong style={{ color: T.espresso }}>Note:</strong>{' '}
        You can customize this to name your institution, the study topic, or
        time estimates. Participants will hear this text before any questions
        begin.
      </div>
      <Nav onBack={onBack} onNext={onNext} nextDisabled={!data.opening?.trim()} />
    </StepWrap>
  );
}
